import React, { useEffect } from 'react';
import { Router, Switch, Route } from 'react-router-dom';
import ShareScreen from '@common/screens/ShareScreen';
import RenderScreen from '@common/screens/RenderScreen';
import history from './history';
import ShareLink from '@common/screens/ShareLink';
import ShareScreenModal from '@common/screens/ShareScreenModal';
import ShareScreenUrl from '@common/screens/ShareScreenUrl';
import useAppDetail from '@common/hooks/app';
import { AppVersion, Platform as PlatformType } from 'click-types';
import { useParams } from '@common/routes/hooks';
import Loading from '@common/components/Loading';
import {
  APP_API_TOKEN,
  LEGACY_TOKEN_KEY,
  getTokenKey,
  setRuntimeAppId,
} from '@common/constants/shared';

// One-shot migration: copy the legacy global `access_token` into the per-app
// namespaced key so users already signed in before this fix shipped are not
// forced to re-authenticate. The legacy key is intentionally left in place
// (other tabs / apps may still need to migrate themselves on next load).
const migrateLegacyToken = (appId: string | undefined) => {
  if (!appId || typeof window === 'undefined') return;
  try {
    const namespaced = getTokenKey(appId);
    if (namespaced === LEGACY_TOKEN_KEY) return;
    if (window.localStorage.getItem(namespaced)) return;
    const legacy = window.localStorage.getItem(LEGACY_TOKEN_KEY);
    if (!legacy || legacy === APP_API_TOKEN) return;
    window.localStorage.setItem(namespaced, legacy);
  } catch {
    // no-op: storage may be unavailable in some browsing modes
  }
};

const useNamespaceTokenForApp = (appId: string | undefined) => {
  useEffect(() => {
    setRuntimeAppId(appId);
    migrateLegacyToken(appId);
  }, [appId]);
};

const NamespaceTokenWrapper: React.FC<{ component: React.ComponentType }> = ({
  component: Component,
}) => {
  const { appId } = useParams();
  useNamespaceTokenForApp(appId);
  return <Component />;
};

const PreviewRoute = () => {
  const { appId } = useParams();
  useNamespaceTokenForApp(appId);
  const { data, isLoading } = useAppDetail({
    appId: appId,
    isShareScreen: true,
  });

  const { version, platform } = data ?? {
    version: AppVersion.V3,
    platform: PlatformType.MOBILE,
  };
  if (isLoading) {
    return <Loading />;
  }
  const isV4Responsive =
    version === AppVersion.V4 && platform === PlatformType.RESPONSIVE;

  if (isV4Responsive) {
    return <ShareScreenUrl />;
  }
  return <RenderScreen />;
};

const AppIdRoute = () => {
  const { appId } = useParams();
  useNamespaceTokenForApp(appId);
  const { data, isLoading } = useAppDetail({
    appId: appId,
    isShareScreen: true,
  });

  const { version, platform } = data ?? {
    version: AppVersion.V3,
    platform: PlatformType.MOBILE,
  };
  if (isLoading) {
    return <Loading />;
  }
  const shouldUseRenderScreen =
    version === AppVersion.V4 && platform === PlatformType.RESPONSIVE;

  if (shouldUseRenderScreen) {
    return <RenderScreen />;
  }
  return <ShareScreen />;
};

export default function AppRoutes() {
  useEffect(() => {
    // Listen for the response
    window.addEventListener('message', function (event) {
      try {
        // Verify the origin of the message
        if (event?.data?.type === 'click__get__cookie') {
          const cookie = event.data.cookie;
          console.log('=== Cookie received:', cookie);
          localStorage.setItem('click__cookie', cookie);
        }
        if (event?.data?.type === 'click__get__current_url') {
          const currentUrl = event.data.currentUrl;
          console.log('=== currentUrl received:', currentUrl);
          localStorage.setItem('click__current_url', currentUrl);
        }
      } catch (error) {
        console.error('=== error', error);
        // do nothing
      }
    });
  }, []);

  return (
    <Router history={history}>
      <Switch>
        <Route path="/share/:shortUrl" component={ShareLink} />
        <Route exact path="/" component={ShareScreen} />
        <Route path="/preview/:appId" component={PreviewRoute} />
        <Route
          path="/preview-v4/:appId"
          render={() => <NamespaceTokenWrapper component={RenderScreen} />}
        />
        <Route
          path="/share-modal/:appId"
          render={() => (
            <NamespaceTokenWrapper component={ShareScreenModal} />
          )}
        />
        <Route
          path="/share-app/:appId"
          render={() => <NamespaceTokenWrapper component={ShareScreenUrl} />}
        />
        <Route path="/:appId" component={AppIdRoute} />
      </Switch>
    </Router>
  );
}
